import { supabase } from '@/integrations/supabase/client';
import { isSystemBotTokenConfigured } from './botTokenService';

/**
 * Check if a user has a Telegram connection
 * @param userId User ID to check
 * @returns Promise<boolean> true if connected, false otherwise
 */
export const checkTelegramConnection = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('telegram_connections')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking Telegram connection:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Failed to check Telegram connection:', error);
    return false;
  }
};

/**
 * Check if Telegram bot is configured
 * @returns boolean true if configured, false otherwise
 */
export const isBotConfigured = async (): Promise<boolean> => {
  return await isSystemBotTokenConfigured();
};

/**
 * Get the chat ID for a user
 * @param userId User ID to get chat ID for
 * @returns Promise with the chat ID or null if not found
 */
export const getUserChatId = async (userId: string): Promise<number | null> => {
  try {
    const { data, error } = await supabase
      .from('telegram_connections')
      .select('telegram_chat_id')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error || !data) {
      console.error('Error getting user chat ID:', error);
      return null;
    }
    
    return data.telegram_chat_id;
  } catch (error) {
    console.error('Failed to get user chat ID:', error);
    return null;
  }
};

/**
 * Generate a connection code for a user
 * @param userId User ID to generate code for
 * @returns Promise with the connection code or null if error
 */
export const generateConnectionCode = async (userId: string): Promise<{ code: string; expires_at: string } | null> => {
  try {
    // Generate a unique connection code
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();
    
    // Set expiration time (24 hours from now)
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);
    
    // First delete any existing unused codes for this user
    await supabase
      .from('telegram_connection_codes')
      .delete()
      .eq('user_id', userId)
      .eq('used', false);
    
    // Insert the new code
    const { data, error } = await supabase
      .from('telegram_connection_codes')
      .insert({
        user_id: userId,
        code,
        expires_at: expiresAt.toISOString()
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error generating connection code:', error);
      return null;
    }
    
    return {
      code,
      expires_at: expiresAt.toISOString()
    };
  } catch (error) {
    console.error('Failed to generate connection code:', error);
    return null;
  }
};
